import { Save, Sparkles } from 'lucide-react'
import { useForm } from 'react-hook-form'

import { companyFormResolver } from '../../lib/form-resolvers'
import { normalizeWebsiteUrl, parseEmailChips } from '../../lib/normalization'
import { Button } from '../ui/Button'
import { Spinner } from '../ui/Spinner'

import { CompanyFormFields } from './CompanyFormFields'

import type { CompanyFormInput, CompanyMutationInput } from '../../types/forms'

interface CreateCompanyFormProps {
  onCreate: (input: CompanyMutationInput) => Promise<void>
}

export function CreateCompanyForm({ onCreate }: CreateCompanyFormProps) {
  const form = useForm<CompanyFormInput>({
    resolver: companyFormResolver,
    defaultValues: {
      name: '',
      businessActivity: '',
      websiteUrl: '',
      emails: { items: [], draft: '' },
      image: null,
    },
  })

  const submitting = form.formState.isSubmitting

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root')
    try {
      const website = normalizeWebsiteUrl(values.websiteUrl)
      const parsedEmails = parseEmailChips(values.emails)
      await onCreate({
        name: values.name,
        businessActivity: values.businessActivity,
        websiteUrl: website.websiteUrl,
        websiteNormalized: website.websiteNormalized,
        emails: parsedEmails.emails,
        image: values.image,
      })
      form.reset()
    } catch (error) {
      form.setError('root.server', {
        message: error instanceof Error ? error.message : 'No fue posible guardar la empresa.',
      })
    }
  })

  return (
    <section className="surface-card overflow-hidden" aria-labelledby="new-company-title">
      <div className="border-b bg-mist/50 px-5 py-5 sm:px-7">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid h-9 w-9 place-items-center rounded-xl bg-moss text-white">
            <Sparkles aria-hidden="true" className="h-4 w-4" />
          </span>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-moss">
              Nuevo registro
            </p>
            <h2
              id="new-company-title"
              className="mt-1 font-display text-2xl font-bold tracking-tight"
            >
              Nueva empresa
            </h2>
            <p className="mt-1 text-sm text-ink/60">
              Completa los datos para agregarla al directorio.
            </p>
          </div>
        </div>
      </div>

      <form className="p-5 sm:p-7" onSubmit={(event) => void submit(event)} noValidate>
        <CompanyFormFields form={form} disabled={submitting} />

        {form.formState.errors.root?.server?.message && (
          <div
            className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800"
            role="alert"
          >
            {form.formState.errors.root.server.message}
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse gap-3 border-t pt-5 sm:flex-row sm:justify-end">
          <Button
            variant="secondary"
            type="button"
            onClick={() => form.reset()}
            disabled={submitting || !form.formState.isDirty}
          >
            Limpiar
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? <Spinner /> : <Save aria-hidden="true" className="h-4 w-4" />}
            {submitting ? 'Guardando…' : 'Guardar empresa'}
          </Button>
        </div>
      </form>
    </section>
  )
}
